import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/utils.js';
import { Button, type ButtonProps } from './button.js';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip.js';

interface IconButtonWithTooltipCommonProps {
  /** Full text shown in the tooltip. Also used as the accessible name unless `ariaLabel` is set. */
  tooltip: string;
  ariaLabel?: string;
  onClick?: () => void;
  disabled?: boolean;
  size?: ButtonProps['size'];
  variant?: ButtonProps['variant'];
  testId?: string;
  tooltipDelayDuration?: number;
}

export type IconButtonWithTooltipProps =
  | (IconButtonWithTooltipCommonProps & {
      /** Render the single `children` element (e.g. a router `Link`) instead of an icon `<button>`. */
      asChild: true;
      children: ReactNode;
      icon?: never;
      active?: never;
      badgeCount?: never;
    })
  | (IconButtonWithTooltipCommonProps & {
      asChild?: false;
      icon: LucideIcon;
      children?: never;
      /** Applies a pressed/active visual style (e.g. a toggle that's currently on). */
      active?: boolean;
      /** Small numeric badge in the top-right corner. Omitted (or <= 0) renders no badge. */
      badgeCount?: number;
    });

/**
 * IconButtonWithTooltip — a compact icon-only button that reveals its full
 * action label via a Tooltip on hover/focus, with an optional small numeric
 * badge (e.g. a selection count).
 *
 * Extracted from the duplicated inline Tooltip+Button+badge scaffolding used
 * for toolbar row actions (selection-mode toggle, quick-add-to-offer) so
 * callers don't have to re-nest TooltipProvider/Tooltip/TooltipTrigger/Button
 * by hand every time.
 *
 * Pass `asChild` with a single child element (e.g. a router `Link`) to render
 * that element instead of an icon `<button>` — mirrors `Button`'s own
 * `asChild`/Radix `Slot` pattern, for toolbar actions that must navigate via a
 * real anchor rather than an onClick handler.
 */
export function IconButtonWithTooltip(props: IconButtonWithTooltipProps) {
  const {
    tooltip,
    ariaLabel,
    onClick,
    disabled,
    active,
    badgeCount,
    size = 'icon-sm',
    variant = 'outline',
    testId,
    tooltipDelayDuration = 300,
  } = props;

  return (
    <TooltipProvider delayDuration={tooltipDelayDuration}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            asChild={props.asChild}
            type={props.asChild ? undefined : 'button'}
            variant={variant}
            size={size}
            className={cn('relative', active ? 'bg-primary/10 text-primary border-primary/30' : '')}
            onClick={onClick}
            disabled={disabled}
            aria-label={ariaLabel ?? tooltip}
            aria-pressed={active}
            data-testid={testId}
          >
            {props.asChild ? (
              props.children
            ) : (
              <>
                <props.icon className="h-4 w-4" />
                {badgeCount != null && badgeCount > 0 ? (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full bg-primary text-primary-foreground text-[10px] font-semibold leading-none flex items-center justify-center px-0.5">
                    {badgeCount}
                  </span>
                ) : null}
              </>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
